import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

import Carousel from '../../../components/carousel';
import GenericCategoryGraph, { chartTypeEnum } from '../../../components/Graphs/GenericCategoryGraph';
import { PageHeader } from '../../../components/PageHeader';
import LoadingRenderer from '../../../components/LoadingRenderer';
import OrgSelect, { NO_ORG } from '../../../components/OrgSelect/OrgSelect';
import Strings from '../../../Strings';
import { DEFAULT_TIMEZONE } from '../../../constants';
import actions from './redux/actions';
import EventDetailsTab from './EventDetailsTab';
import FailCodeAnalysisTab from './FailCodeAnalysisTab';
import FailedEventsTab from './FailedEventsTab';

import './MessagesOverview.scss';
import './SuperuserHealthCenter.scss';

const EVENT_RANGE_PRESETS = [
  { value: 'last_1h', label: 'Hourly' },
  { value: 'last_24h', label: '24 hours' },
  { value: 'last_7d', label: '7 days' },
  { value: 'last_28d', label: '28 days' },
];

const EVENT_PAGE_TABS = {
  Overview: 'Overview',
  EventDetails: 'Event Details',
  FailedEvents: 'Failed Events',
  FailCodeAnalysis: 'FAIL Code Analysis',
};

function getPresetDurationMs(preset) {
  switch (preset) {
    case 'last_1h':
      return 60 * 60 * 1000;
    case 'last_24h':
      return 24 * 60 * 60 * 1000;
    case 'last_7d':
      return 7 * 24 * 60 * 60 * 1000;
    case 'last_28d':
    case 'last_30d':
      return 28 * 24 * 60 * 60 * 1000;
    default:
      return 24 * 60 * 60 * 1000;
  }
}

function formatRangeLabel(startAt, endAt) {
  const sameDay = startAt.toDateString() === endAt.toDateString();
  const dateFmt = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
  const dateTimeFmt = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  if (sameDay) {
    return `${dateTimeFmt.format(startAt)} - ${new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    }).format(endAt)}`;
  }
  return `${dateFmt.format(startAt)} - ${dateTimeFmt.format(endAt)}`;
}

function formatShortRangeLabel(startAt, endAt) {
  const durationMs = endAt.getTime() - startAt.getTime();
  const sameDay = startAt.toDateString() === endAt.toDateString();
  const sameYear = startAt.getFullYear() === endAt.getFullYear();
  const dateFormatter = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  const timeFormatter = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });

  if (durationMs <= 24 * 60 * 60 * 1000) {
    if (sameDay) {
      return `${dateFormatter.format(startAt)}, ${timeFormatter.format(startAt)} - ${timeFormatter.format(endAt)}`;
    }

    return `${dateFormatter.format(startAt)}, ${timeFormatter.format(startAt)} - ${dateFormatter.format(endAt)}, ${timeFormatter.format(endAt)}`;
  }

  if (sameDay) {
    return dateFormatter.format(startAt);
  }

  return `${dateFormatter.format(startAt)} - ${dateFormatter.format(endAt)}`;
}

function SummaryCard({ title, value, subtitle, accent }) {
  return (
    <div className="health-messages-card">
      <div className="health-messages-card__label">{title}</div>
      <div className="health-messages-card__value">{value}</div>
      <div className={`health-messages-card__meta ${accent ? `is-${accent}` : ''}`}>{subtitle}</div>
    </div>
  );
}

SummaryCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  subtitle: PropTypes.node.isRequired,
  accent: PropTypes.string,
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="health-messages-tooltip">
      <div className="health-messages-tooltip__title">{label}</div>
      {payload.map(entry => (
        <div key={entry.name} className="health-messages-tooltip__row">
          <span className="health-messages-tooltip__label">{entry.name}</span>
          <span className="health-messages-tooltip__value">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

ChartTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.string,
};

function MessageVolumeChart({
  title,
  data,
  currentLabel,
  previousLabel,
  color,
  previousColor = '#8fa3b8',
  compactTicks,
  onCurrentClick,
  onPreviousClick,
}) {
  return (
    <div className="health-messages-panel">
      <div className="health-messages-panel__header">
        <div className="health-messages-panel__title">{title}</div>
      </div>
      <div className="health-messages-panel__body is-chart">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data} barGap={8} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e6edf2" />
            <XAxis
              dataKey="label"
              tick={{ fill: '#54606c', fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              interval={0}
              minTickGap={compactTicks ? 0 : 8}
              tickFormatter={value => {
                if (!compactTicks) return value;
                return value;
              }}
            />
            <YAxis tick={{ fill: '#54606c', fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip content={<ChartTooltip />} />
            <Legend />
            <Bar
              dataKey="current"
              name={currentLabel}
              fill={color}
              radius={[6, 6, 0, 0]}
              onClick={barData => onCurrentClick?.(barData?.payload)}
              cursor={onCurrentClick ? 'pointer' : 'default'}
            />
            <Bar
              dataKey="previous"
              name={previousLabel}
              fill={previousColor}
              radius={[6, 6, 0, 0]}
              onClick={barData => onPreviousClick?.(barData?.payload)}
              cursor={onPreviousClick ? 'pointer' : 'default'}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

MessageVolumeChart.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  currentLabel: PropTypes.string.isRequired,
  previousLabel: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  previousColor: PropTypes.string,
  compactTicks: PropTypes.bool,
  onCurrentClick: PropTypes.func,
  onPreviousClick: PropTypes.func,
};

function HorizontalBreakdownChart({ title, data, dataKey, valueLabel, color, colors, yAxisWidth, onBarClick }) {
  return (
    <div className="health-messages-panel">
      <div className="health-messages-panel__header">
        <div className="health-messages-panel__title">{title}</div>
      </div>
      <div className="health-messages-panel__body is-chart">
        <GenericCategoryGraph
          graphData={data}
          graphType={chartTypeEnum.horizontalBar}
          dataSeries={[{ name: valueLabel, color: colors?.[0] || color, selector: item => item[dataKey] }]}
          categorySelector={item => item.label}
          tooltipTitle={title}
          yAxisWidth={yAxisWidth || 220}
          onCategoryClicked={label => onBarClick?.(data.find(item => item.label === label))}
        />
      </div>
    </div>
  );
}

HorizontalBreakdownChart.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  dataKey: PropTypes.string.isRequired,
  valueLabel: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  colors: PropTypes.arrayOf(PropTypes.string),
  yAxisWidth: PropTypes.number,
  onBarClick: PropTypes.func,
};

function formatCompactNumber(value) {
  return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(value || 0);
}

function formatPercentWithCount(label, count, rate) {
  return `${((rate || 0) * 100).toFixed(1)}% ${label} (${formatCompactNumber(count)})`;
}

function renderSummarySubtitle(data) {
  return (
    <div className="health-events-card-meta">
      <div className="health-events-card-meta__primary">
        {formatPercentWithCount('vitals', data?.vital_events, data?.vital_rate)}
      </div>
      <div className="health-events-card-meta__danger">
        {formatPercentWithCount('failed', data?.failed_events, data?.failed_rate)}
      </div>
    </div>
  );
}

function normalizeHwCodeItems(items) {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map(item => ({
    key: item.hw_code,
    label: item.hw_code,
    count: item.count,
    hw_code: item.hw_code,
  }));
}

function normalizeOrgItems(items) {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map(item => ({
    key: `${item.org_id}-${item.org_name}`,
    label: item.org_name,
    count: item.all_events,
    allEvents: item.all_events,
    vitalEvents: item.vital_events,
    org_id: item.org_id,
    org_name: item.org_name,
  }));
}

function buildDrilldownBuckets(series, endAtMs, bucket) {
  if (!series?.length) {
    return [];
  }

  if (bucket === 'hour') {
    const endHourMs = new Date(endAtMs).setMinutes(0, 0, 0);
    const startHourMs = endHourMs - (23 * 60 * 60 * 1000);
    return series.map((item, index) => {
      const bucketStartMs = startHourMs + (index * 60 * 60 * 1000);
      const bucketEndMs = bucketStartMs + (60 * 60 * 1000);
      return {
        ...item,
        current_start_at: new Date(bucketStartMs).toISOString(),
        current_end_at: new Date(bucketEndMs).toISOString(),
        previous_start_at: new Date(bucketStartMs - (24 * 60 * 60 * 1000)).toISOString(),
        previous_end_at: new Date(bucketEndMs - (24 * 60 * 60 * 1000)).toISOString(),
      };
    });
  }

  const currentEndDate = new Date(endAtMs);
  currentEndDate.setHours(0, 0, 0, 0);
  const startDayMs = currentEndDate.getTime() - (6 * 24 * 60 * 60 * 1000);
  const previousStartDayMs = startDayMs - (7 * 24 * 60 * 60 * 1000);
  return series.map((item, index) => {
    const bucketStartMs = startDayMs + (index * 24 * 60 * 60 * 1000);
    const bucketEndMs = bucketStartMs + (24 * 60 * 60 * 1000);
    const previousBucketStartMs = previousStartDayMs + (index * 24 * 60 * 60 * 1000);
    return {
      ...item,
      current_start_at: new Date(bucketStartMs).toISOString(),
      current_end_at: new Date(bucketEndMs).toISOString(),
      previous_start_at: new Date(previousBucketStartMs).toISOString(),
      previous_end_at: new Date(previousBucketStartMs + (24 * 60 * 60 * 1000)).toISOString(),
    };
  });
}

function buildSinglePeriodComparison({
  currentValue,
  previousValue,
  currentStartAt,
  currentEndAt,
  previousStartAt,
  previousEndAt,
}) {
  return [{
    label: 'Selected period',
    current: currentValue || 0,
    previous: previousValue || 0,
    current_start_at: currentStartAt,
    current_end_at: currentEndAt,
    previous_start_at: previousStartAt,
    previous_end_at: previousEndAt,
  }];
}

function EventsOverview({ getEventsOverview, orgIds }) {
  const [activeTab, setActiveTab] = React.useState(EVENT_PAGE_TABS.Overview);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [overview, setOverview] = React.useState(null);
  const [drilldownRequest, setDrilldownRequest] = React.useState(null);
  const [rangePreset, setRangePreset] = React.useState('last_24h');
  const [rangeEndAt, setRangeEndAt] = React.useState(null);
  const [refreshNonce, setRefreshNonce] = React.useState(0);
  const getEventsOverviewRef = React.useRef(getEventsOverview);

  const timezone = React.useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE, []);
  const rangeDurationMs = React.useMemo(() => getPresetDurationMs(rangePreset), [rangePreset]);
  const effectiveEndAtMs = React.useMemo(() => (rangeEndAt === null ? Date.now() : rangeEndAt), [rangeEndAt]);
  const effectiveStartAtMs = effectiveEndAtMs - rangeDurationMs;
  const isAtLatestRange = rangeEndAt === null;
  const rangeLabel = React.useMemo(
    () => formatRangeLabel(new Date(effectiveStartAtMs), new Date(effectiveEndAtMs)),
    [effectiveEndAtMs, effectiveStartAtMs],
  );

  React.useEffect(() => {
    getEventsOverviewRef.current = getEventsOverview;
  }, [getEventsOverview]);

  const requestParams = React.useMemo(() => {
    const params =
      rangeEndAt === null
        ? { timezone, range_mode: 'presets', preset: rangePreset }
        : {
            timezone,
            range_mode: 'custom',
            start_at: new Date(effectiveStartAtMs).toISOString(),
            end_at: new Date(effectiveEndAtMs).toISOString(),
          };
    if (orgIds && orgIds.length > 0 && !orgIds.includes(NO_ORG)) {
      params.org_uuid = orgIds;
    }
    return params;
  }, [effectiveEndAtMs, effectiveStartAtMs, orgIds, rangeEndAt, rangePreset, timezone]);
  const requestKey = React.useMemo(
    () => JSON.stringify({ ...requestParams, refreshNonce }),
    [requestParams, refreshNonce],
  );

  React.useEffect(() => {
    setIsLoading(true);
    setError('');

    getEventsOverviewRef.current(requestParams)
      .then(resp => {
        setOverview(resp?.response || null);
        setIsLoading(false);
      })
      .catch(() => {
        setError('Unable to load event monitor data.');
        setIsLoading(false);
      });
  }, [requestKey, requestParams]);

  const moveRangeLeft = React.useCallback(() => {
    setRangeEndAt(prev => (prev ?? Date.now()) - rangeDurationMs);
  }, [rangeDurationMs]);

  const moveRangeRight = React.useCallback(() => {
    setRangeEndAt(prev => {
      if (prev === null) return null;
      const candidate = prev + rangeDurationMs;
      if (candidate >= Date.now()) return null;
      return candidate;
    });
  }, [rangeDurationMs]);

  const onPresetChange = React.useCallback(event => {
    setRangePreset(event.target.value);
    setRangeEndAt(null);
  }, []);

  const onRefresh = React.useCallback(() => {
    setRefreshNonce(prev => prev + 1);
  }, []);

  const summary = overview?.summary || {};
  const appliedFilters = overview?.filters || {};
  const breakdowns = overview?.breakdowns || {};
  const timeseries = overview?.timeseries?.current_vs_previous?.series || {};
  const timeseriesBucket = overview?.timeseries?.current_vs_previous?.bucket || 'hour';

  const summaryCards = [
    {
      title: 'Current range',
      data: summary.current,
    },
    {
      title: 'Previous range',
      data: summary.previous,
    },
  ];

  const hwCodeItems = normalizeHwCodeItems(breakdowns.hw_codes);
  const orgItems = normalizeOrgItems(breakdowns.organizations);
  const vitalHwCodes = overview?.vital_hw_codes || [];
  const allEventsSeries = React.useMemo(() => {
    if (rangePreset === 'last_1h') {
      return buildSinglePeriodComparison({
        currentValue: summary.current?.all_events,
        previousValue: summary.previous?.all_events,
        currentStartAt: appliedFilters.start_at,
        currentEndAt: appliedFilters.end_at,
        previousStartAt: appliedFilters.previous_start_at,
        previousEndAt: appliedFilters.previous_end_at,
      });
    }

    return buildDrilldownBuckets(timeseries.all_events || [], effectiveEndAtMs, timeseriesBucket);
  }, [
    appliedFilters.end_at,
    appliedFilters.previous_end_at,
    appliedFilters.previous_start_at,
    appliedFilters.start_at,
    effectiveEndAtMs,
    rangePreset,
    summary.current?.all_events,
    summary.previous?.all_events,
    timeseries.all_events,
    timeseriesBucket,
  ]);
  const vitalEventsSeries = React.useMemo(() => {
    if (rangePreset === 'last_1h') {
      return buildSinglePeriodComparison({
        currentValue: summary.current?.vital_events,
        previousValue: summary.previous?.vital_events,
        currentStartAt: appliedFilters.start_at,
        currentEndAt: appliedFilters.end_at,
        previousStartAt: appliedFilters.previous_start_at,
        previousEndAt: appliedFilters.previous_end_at,
      });
    }

    return buildDrilldownBuckets(timeseries.vital_events || [], effectiveEndAtMs, timeseriesBucket);
  }, [
    appliedFilters.end_at,
    appliedFilters.previous_end_at,
    appliedFilters.previous_start_at,
    appliedFilters.start_at,
    effectiveEndAtMs,
    rangePreset,
    summary.current?.vital_events,
    summary.previous?.vital_events,
    timeseries.vital_events,
    timeseriesBucket,
  ]);
  const chartRangeLabels = React.useMemo(() => {
    const currentStart = overview?.filters?.start_at ? new Date(overview.filters.start_at) : new Date(effectiveStartAtMs);
    const currentEnd = overview?.filters?.end_at ? new Date(overview.filters.end_at) : new Date(effectiveEndAtMs);
    const previousStart = overview?.filters?.previous_start_at ? new Date(overview.filters.previous_start_at) : new Date(effectiveStartAtMs - rangeDurationMs);
    const previousEnd = overview?.filters?.previous_end_at ? new Date(overview.filters.previous_end_at) : new Date(effectiveStartAtMs);

    return {
      current: formatShortRangeLabel(currentStart, currentEnd),
      previous: formatShortRangeLabel(previousStart, previousEnd),
      title: `${formatShortRangeLabel(currentStart, currentEnd)} vs ${formatShortRangeLabel(previousStart, previousEnd)}`,
    };
  }, [
    effectiveEndAtMs,
    effectiveStartAtMs,
    overview?.filters?.end_at,
    overview?.filters?.previous_end_at,
    overview?.filters?.previous_start_at,
    overview?.filters?.start_at,
    rangeDurationMs,
  ]);
  const eventTabs = Object.values(EVENT_PAGE_TABS);

  const openEventDetails = React.useCallback(filters => {
    setDrilldownRequest({
      ...filters,
      key: JSON.stringify({ ...filters, ts: Date.now() }),
    });
    setActiveTab(EVENT_PAGE_TABS.EventDetails);
  }, []);

  const openTimeseriesDrilldown = React.useCallback((payload, periodKey, extraFilters = {}) => {
    if (!payload) {
      return;
    }

    openEventDetails({
      start_at: payload[`${periodKey}_start_at`],
      end_at: payload[`${periodKey}_end_at`],
      ...extraFilters,
    });
  }, [openEventDetails]);

  const openBreakdownDrilldown = React.useCallback(extraFilters => {
    openEventDetails({
      start_at: new Date(effectiveStartAtMs).toISOString(),
      end_at: new Date(effectiveEndAtMs).toISOString(),
      ...extraFilters,
    });
  }, [effectiveEndAtMs, effectiveStartAtMs, openEventDetails]);

  const goBackToOverview = React.useCallback(() => {
    setActiveTab(EVENT_PAGE_TABS.Overview);
  }, []);

  return (
    <div className="health-messages health-events">
      <div className="tab-view">
        <div className="tabs">
          <Carousel type="navMenu" activeTabName={activeTab}>
            {eventTabs.map(tabName => (
              <a
                key={tabName}
                className={activeTab === tabName ? 'active' : ''}
                onClick={() => setActiveTab(tabName)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setActiveTab(tabName);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                {tabName}
              </a>
            ))}
          </Carousel>
        </div>

        <div className={`tab-content ${activeTab === EVENT_PAGE_TABS.Overview ? 'active health-events__scrollable' : ''}`}>
          <PageHeader
            growRight
            left="Events overview"
            right={(
              <div className="health-messages__header-controls">
                <div className="health-messages__range-nav health-messages__range-nav--centered">
                  <button type="button" className="health-messages__range-nav-button" onClick={moveRangeLeft}>
                    &larr;
                  </button>
                  <div className="health-messages__range-label">{rangeLabel}</div>
                  <button
                    type="button"
                    className="health-messages__range-nav-button"
                    onClick={moveRangeRight}
                    disabled={isAtLatestRange}
                  >
                    &rarr;
                  </button>
                </div>
                <div className="health-messages__header-actions">
                  <select
                    className="health-messages__range-select"
                    value={rangePreset}
                    onChange={onPresetChange}
                  >
                    {EVENT_RANGE_PRESETS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <OrgSelect />
                  <button type="button" className="health-messages__refresh" onClick={onRefresh}>
                    {Strings.refresh || 'Refresh'}
                  </button>
                </div>
              </div>
            )}
          />
          <div className="health-messages__subtitle health-messages__subtitle--page">
            All event volume, vital event share, and organization-level device event trends.
          </div>

          <LoadingRenderer loading={isLoading}>
            <>
              {error ? <div className="health-messages-error">{error}</div> : null}

              {!error && overview ? (
                <>
                  <div className="health-messages-grid health-messages-grid--summary">
                    {summaryCards.map(card => (
                      <SummaryCard
                        key={card.title}
                        title={card.title}
                        value={`${formatCompactNumber(card.data?.all_events)} / ${formatCompactNumber(card.data?.vital_events)}`}
                        subtitle={renderSummarySubtitle(card.data)}
                      />
                    ))}
                  </div>

                  <div className="health-messages-grid health-messages-grid--single-chart">
                    <MessageVolumeChart
                      title={`All events: ${chartRangeLabels.title}`}
                      data={allEventsSeries}
                      currentLabel={chartRangeLabels.current}
                      previousLabel={chartRangeLabels.previous}
                      color="#205375"
                      compactTicks
                      onCurrentClick={payload => openTimeseriesDrilldown(payload, 'current')}
                      onPreviousClick={payload => openTimeseriesDrilldown(payload, 'previous')}
                    />
                  </div>

                  <div className="health-messages-grid health-messages-grid--single-chart">
                    <MessageVolumeChart
                      title={`Vital events: ${chartRangeLabels.title}`}
                      data={vitalEventsSeries}
                      currentLabel={chartRangeLabels.current}
                      previousLabel={chartRangeLabels.previous}
                      color="#4d908e"
                      onCurrentClick={payload => openTimeseriesDrilldown(payload, 'current', { hw_codes: vitalHwCodes })}
                      onPreviousClick={payload => openTimeseriesDrilldown(payload, 'previous', { hw_codes: vitalHwCodes })}
                    />
                  </div>

                  <div className="health-messages-grid health-messages-grid--breakdowns">
                    <HorizontalBreakdownChart
                      title="Top HW codes"
                      data={hwCodeItems}
                      dataKey="count"
                      valueLabel="Events"
                      color="#205375"
                      colors={['#205375', '#3d5a80', '#98c1d9', '#ee6c4d', '#7b9e87', '#8d99ae', '#457b9d']}
                      yAxisWidth={120}
                      onBarClick={payload => openBreakdownDrilldown({ hw_codes: payload?.hw_code ? [payload.hw_code] : [] })}
                    />
                  </div>

                  <div className="health-messages-grid health-messages-grid--orgs">
                    <HorizontalBreakdownChart
                      title="Top organizations"
                      data={orgItems}
                      dataKey="count"
                      valueLabel="Events"
                      color="#4d908e"
                      colors={['#4d908e', '#2c7da0', '#90be6d', '#f9844a', '#577590', '#43aa8b', '#277da1']}
                      yAxisWidth={220}
                      onBarClick={payload => openBreakdownDrilldown({ org_ids: payload?.org_id ? [payload.org_id] : [] })}
                    />
                  </div>
                </>
              ) : null}
            </>
          </LoadingRenderer>
        </div>

        <div className={`tab-content ${activeTab === EVENT_PAGE_TABS.EventDetails ? 'active' : ''}`}>
          <EventDetailsTab drilldownRequest={drilldownRequest} onBack={goBackToOverview} />
        </div>

        <div className={`tab-content ${activeTab === EVENT_PAGE_TABS.FailedEvents ? 'active' : ''}`}>
          <FailedEventsTab />
        </div>

        <div className={`tab-content ${activeTab === EVENT_PAGE_TABS.FailCodeAnalysis ? 'active health-events__scrollable' : ''}`}>
          <FailCodeAnalysisTab onOpenEventDetails={openEventDetails} />
        </div>
      </div>
    </div>
  );
}

EventsOverview.propTypes = {
  getEventsOverview: PropTypes.func.isRequired,
  orgIds: PropTypes.arrayOf(PropTypes.string),
};

const mapStateToProps = state => ({
  orgIds: state.entities?.orgFilter?.orgIds,
});

const mapDispatchToProps = dispatch => ({
  getEventsOverview: params => dispatch(actions.getEventsOverview(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EventsOverview);
